import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Image, Upload, X, GripVertical, AlertCircle, Film } from "lucide-react";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const MAX_FILES = 20;

export default function VisualsUploader({ visuals, setVisuals }) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const validateAndAddFiles = async (files) => {
    setError(null);
    const newVisuals = [];

    for (const file of files) {
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

      if (!isImage && !isVideo) {
        setError("Only JPG, PNG, WEBP images and MP4, MOV videos are allowed");
        continue;
      }

      if (visuals.length + newVisuals.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files allowed`);
        break;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      newVisuals.push({
        id: crypto.randomUUID(),
        file,
        previewUrl,
        type: isImage ? "image" : "video",
      });
    }

    if (newVisuals.length > 0) {
      setVisuals([...visuals, ...newVisuals]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndAddFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      validateAndAddFiles(Array.from(e.target.files));
    }
    e.target.value = "";
  };

  const removeVisual = (id) => {
    const visual = visuals.find((v) => v.id === id);
    if (visual) {
      URL.revokeObjectURL(visual.previewUrl);
    }
    setVisuals(visuals.filter((v) => v.id !== id));
  };

  const moveVisual = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= visuals.length) return;
    
    const newVisuals = [...visuals];
    [newVisuals[fromIndex], newVisuals[toIndex]] = [newVisuals[toIndex], newVisuals[fromIndex]];
    setVisuals(newVisuals);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] mb-4">
          <Image className="w-8 h-8 text-cyan-400" />
        </div>
        <h2 className="text-2xl font-bold text-[#e0e0e0] mb-2">Add Your Visuals</h2>
        <p className="text-[#808080]">
          Upload images and videos for your slideshow (up to {MAX_FILES})
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          isDragging
            ? "border-cyan-500 bg-cyan-500/10"
            : "border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#0a0a0a]"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.mp4,.mov"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Upload className="w-10 h-10 text-[#404040] mx-auto mb-3" />
        <p className="text-[#808080] mb-1">
          Drag & drop images or videos here
        </p>
        <p className="text-sm text-[#505050]">
          JPG, PNG, WEBP, MP4, MOV
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Visuals Grid */}
      {visuals.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#a0a0a0]">
              {visuals.length} visual{visuals.length !== 1 ? "s" : ""} added
            </span>
            <span className="text-xs text-[#606060]">
              Drag to reorder
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {visuals.map((visual, index) => (
              <div
                key={visual.id}
                className="relative group aspect-square bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg overflow-hidden"
              >
                {visual.type === "image" ? (
                  <img
                    src={visual.previewUrl}
                    alt={`Visual ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="relative w-full h-full">
                    <video
                      src={visual.previewUrl}
                      className="w-full h-full object-cover"
                      muted
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Film className="w-8 h-8 text-white/80" />
                    </div>
                  </div>
                )}
                
                {/* Index badge */}
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                  {index + 1}
                </div>

                {/* Controls overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); moveVisual(index, -1); }}
                    disabled={index === 0}
                    className="w-8 h-8 text-white hover:bg-white/20"
                  >
                    <GripVertical className="w-4 h-4 rotate-90" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); removeVisual(visual.id); }}
                    className="w-8 h-8 text-red-400 hover:bg-red-500/20"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[#a0a0a0] mb-2">Tips:</h4>
        <ul className="text-xs text-[#606060] space-y-1">
          <li>• Use high-resolution images for best quality</li>
          <li>• Square images (1:1) work best for lyric videos</li>
          <li>• Images will rotate in order during playback</li>
          <li>• Short video clips can add dynamic visual interest</li>
        </ul>
      </div>
    </div>
  );
}
